import { z } from 'zod';
import { Vals } from '~lib';

/**
 * @desc Building blocks shared by the UI round 2 data contracts (W0.2): slugs, the subjects a
 * page or event can be about, and offset paging. Kept to what more than one contract uses.
 */
export namespace Common {
  /** @desc Longest slug any contract accepts — generous next to the longest fixture slug. */
  export const MAX_SLUG_LENGTH = 120;

  /**
   * @desc A page slug: lowercase ASCII words joined by single hyphens (`flutter-entertainment`,
   * `us-new-jersey`, `888sport`). The same spelling the portal's routes and fixtures use.
   */
  export const slugSchema = z
    .string()
    .min(1)
    .max(MAX_SLUG_LENGTH)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Expected a lowercase, hyphen-separated slug');

  /** @desc What a portal page (and so a follow, an event or a note) can be about. */
  export const subjectTypes = {
    Operator: 'operator',
    Region: 'region',
  } as const;
  export type SubjectType = Vals<typeof subjectTypes>;
  export const ALL_SUBJECT_TYPES = Object.values(subjectTypes) as [SubjectType, ...SubjectType[]];

  /**
   * @desc A cross-link to an operator group's page or a region's page, with its display name.
   *
   * The portal fixtures call this `SubjectRef` and spell the discriminator `kind`; the contracts
   * say `type` (as `Follows.Follow.entityType` and `Activity.Event.entity.type` do), so a fixture
   * swap renames that one key.
   */
  export const subjectSchema = z.object({
    type: z.enum(ALL_SUBJECT_TYPES),
    slug: slugSchema,
    name: z.string().min(1),
  });
  export type Subject = z.infer<typeof subjectSchema>;

  /** @desc A person credited with a piece of content. `role` is their job title, when known. */
  export const authorSchema = z.object({
    name: z.string().min(1),
    role: z.string().min(1).optional(),
  });
  export type Author = z.infer<typeof authorSchema>;

  /** @desc Default page size where a caller does not choose one — the listings' default view. */
  export const DEFAULT_PAGE_SIZE = 24;

  /**
   * @desc Offset paging input: a 1-based page number and a page size capped at `maxLimit`.
   *
   * Offset rather than cursor paging because every consumer renders numbered pages and a record
   * count ("Showing 24 of 118"), which a cursor cannot give without a second query.
   */
  export const pageInput = (maxLimit: number) => ({
    page: z.number().int().min(1).default(1),
    limit: z.number().int().min(1).max(maxLimit).default(DEFAULT_PAGE_SIZE),
  });

  /** @desc One page of `item`s with the paging that produced it and the unpaged total. */
  export const pageOf = <T extends z.ZodType>(item: T) =>
    z.object({
      items: z.array(item),
      page: z.number().int().min(1),
      limit: z.number().int().min(1),
      /** @desc Matching records across all pages. */
      total: z.number().int().min(0),
    });
  export type Page<T> = { items: T[]; page: number; limit: number; total: number };
}
